import React, { useState, useRef, useEffect, useContext } from "react";
import authFetch from "../../authFetch";
import PlatformCtx from "../../state/PlatformCtx";
import T from "../../i18n/translations";

const AI_MODELS = [
  {
    id: "claude-sonnet",
    label: "Claude Sonnet",
    provider: "anthropic",
    model: "claude-sonnet-4-20250514",
    color: "#C9A84C",
    badge: "Claude",
    badgeColor: "#C9A84C",
    badgeBg: "#FDFBF4",
    description: "Anthropic · Balanced, fast, high quality",
    available: true,
  },
  {
    id: "claude-opus",
    label: "Claude Opus",
    provider: "anthropic",
    model: "claude-opus-4-6",
    color: "#B07020",
    badge: "Claude",
    badgeColor: "#B07020",
    badgeBg: "#FDF6EC",
    description: "Anthropic · Most capable, complex tasks",
    available: true,
  },
  {
    id: "gpt4o",
    label: "GPT-4o",
    provider: "openai",
    model: "gpt-4o",
    color: "#2D7D52",
    badge: "OpenAI",
    badgeColor: "#2D7D52",
    badgeBg: "#F0F8F4",
    description: "OpenAI · Requires OPENAI_API_KEY in production",
    available: false,
  },
  {
    id: "gpt4o-mini",
    label: "GPT-4o Mini",
    provider: "openai",
    model: "gpt-4o-mini",
    color: "#2D7D52",
    badge: "OpenAI",
    badgeColor: "#2D7D52",
    badgeBg: "#F0F8F4",
    description: "OpenAI · Faster, cost-efficient",
    available: false,
  },
  {
    id: "gemini-pro",
    label: "Gemini Pro",
    provider: "google",
    model: "gemini-1.5-pro",
    color: "#2B5EA7",
    badge: "Google",
    badgeColor: "#2B5EA7",
    badgeBg: "#F0F4FC",
    description: "Google · Requires GOOGLE_AI_API_KEY in production",
    available: false,
  },
  {
    id: "gemini-flash",
    label: "Gemini Flash",
    provider: "google",
    model: "gemini-1.5-flash",
    color: "#2B5EA7",
    badge: "Google",
    badgeColor: "#2B5EA7",
    badgeBg: "#F0F4FC",
    description: "Google · Fast, multimodal",
    available: false,
  },
];

/* Agent definitions */
const AGENTS=[
  {id:"exec",   label:"Executive Assistant", labelAr:"المساعد التنفيذي", defaultModel:"claude-sonnet", system:"You are TR's executive assistant. Help with tasks, priorities, decisions, scheduling, and follow-ups. Be sharp, concise, and actionable."},
  {id:"invest", label:"Investment Analyst",  labelAr:"محلل الاستثمار",   defaultModel:"claude-opus",   system:"You are TR's investment analyst. Discuss portfolio, market conditions, deal evaluation, and financial strategy. Be analytical and data-driven."},
  {id:"house",  label:"Household AI",        labelAr:"مساعد المنزل",     defaultModel:"claude-sonnet", system:"You are TR's household assistant. Help with staff management, groceries, maintenance scheduling, and home operations."},
  {id:"research",label:"Research & Writing", labelAr:"البحث والكتابة",   defaultModel:"claude-sonnet",  system:"You are TR's research and writing assistant. Help with analysis, document drafting, summaries, and knowledge tasks."},
  {id:"custom", label:"Custom Agent",        labelAr:"وكيل مخصص",       defaultModel:"claude-sonnet", system:""},
];

async function sendToModel(modelId, systemPrompt, messages) {
  const model = AI_MODELS.find(m => m.id === modelId) || AI_MODELS[0];
  const claudeModel = model.provider === "anthropic" ? model.model : "claude-sonnet-4-20250514";
  const r = await authFetch("/api/ai-chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: claudeModel,
      max_tokens: 1200,
      system: systemPrompt || "You are a helpful assistant.",
      messages,
    })
  });
  const d = await r.json();
  if (d.error) throw new Error(d.error);
  return d.content?.[0]?.text || "No response.";
}

function ModelBadge({ modelId, size="sm" }) {
  const model = AI_MODELS.find(m => m.id === modelId);
  if (!model) return null;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4,
      background: model.badgeBg,
      color: model.badgeColor,
      border: `1px solid ${model.badgeColor}33`,
      borderRadius: 20,
      padding: size==="xs" ? "1px 7px" : "2px 9px",
      fontSize: size==="xs" ? 10 : 11,
      fontWeight: 600,
      fontFamily: "var(--fm)",
      letterSpacing: "0.02em",
      whiteSpace: "nowrap",
    }}>
      <span style={{width:5,height:5,borderRadius:"50%",background:model.badgeColor,flexShrink:0}}/>
      {model.label}
    </span>
  );
}

function AIWorkspace({lang, wsApiKeys}){
  const isRTL = lang === "ar";
  const t = T[lang === "ar" ? "ar" : "en"];
  const platformCtx = useContext(PlatformCtx);
  /* Effective Anthropic key: workspace key overrides platform key */
  const effectiveAnthropicKey = (wsApiKeys||{}).anthropic || (platformCtx?.platformApiKeys?.anthropic) || "";

  // Per-agent selected model (persisted in localStorage via separate key)
  const [agentModels, setAgentModels] = useState(() => {
    try {
      const saved = localStorage.getItem("tros_agent_models");
      if (saved) return JSON.parse(saved);
    } catch {}
    const defaults = {};
    AGENTS.forEach(a => { defaults[a.id] = a.defaultModel; });
    return defaults;
  });

  const [activeAgent, setActiveAgent] = useState("exec");
  const [conversations, setConversations] = useState({});
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingAgent, setLoadingAgent] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [editingCustom, setEditingCustom] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [promptLib, setPromptLib] = useState([
    {id:"p1", label:"Summarise my week",    labelAr:"لخّص أسبوعي",           text:"Give me a structured summary of key priorities, decisions made, and what is still open this week.", textAr:"قدّم لي ملخصاً منظماً لأهم الأولويات والقرارات المتخذة وما تبقى مفتوحاً هذا الأسبوع."},
    {id:"p2", label:"Investment review",    labelAr:"مراجعة الاستثمارات",    text:"Give me a high-level review of my portfolio positioning and any risks I should be aware of.",            textAr:"قدّم لي مراجعة عامة لتموضع محفظتي الاستثمارية وأي مخاطر ينبغي أن أنتبه لها."},
    {id:"p3", label:"Draft a brief",        labelAr:"صياغة موجز",            text:"Draft a concise 1-page executive brief on the following topic:",                                         textAr:"اصغ موجزاً تنفيذياً من صفحة واحدة حول الموضوع التالي:"},
    {id:"p4", label:"Pros & cons",          labelAr:"الإيجابيات والسلبيات",  text:"Give me a structured pros and cons analysis for the following decision:",                                textAr:"قدّم لي تحليلاً منظماً للإيجابيات والسلبيات للقرار التالي:"},
    {id:"p5", label:"Prepare for meeting",  labelAr:"تحضير لاجتماع",         text:"Help me prepare talking points and key questions for a meeting about:",                                  textAr:"ساعدني في تحضير نقاط النقاش والأسئلة الرئيسية لاجتماع حول:"},
  ]);
  const [newPLabel, setNewPLabel] = useState("");
  const [newPText, setNewPText] = useState("");
  const [showAddP, setShowAddP] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const messagesEnd = useRef(null);

  const agent = AGENTS.find(a => a.id === activeAgent);
  const msgs = conversations[activeAgent] || [];
  const selectedModelId = agentModels[activeAgent] || agent?.defaultModel || "claude-sonnet";
  const selectedModel = AI_MODELS.find(m => m.id === selectedModelId) || AI_MODELS[0];

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, loading]);

  useEffect(() => {
    try { localStorage.setItem("tros_agent_models", JSON.stringify(agentModels)); } catch {}
  }, [agentModels]);

  const setAgentModel = (agentId, modelId) => {
    setAgentModels(prev => ({ ...prev, [agentId]: modelId }));
  };

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    const sys = activeAgent === "custom" ? customPrompt : agent.system;
    const next = [...msgs, { role: "user", content: msg, model: selectedModelId }];
    setConversations(p => ({ ...p, [activeAgent]: next }));
    setLoading(true);
    setLoadingAgent(activeAgent);
    try {
      const reply = await sendToModel(selectedModelId, sys, next.map(m => ({ role: m.role, content: m.content })));
      setConversations(p => ({
        ...p,
        [activeAgent]: [...next, { role: "assistant", content: reply, model: selectedModelId }]
      }));
    } catch (e) {
      setConversations(p => ({
        ...p,
        [activeAgent]: [...next, { role: "assistant", content: `${isRTL?"خطأ":"Error"}: ${e.message}`, model: selectedModelId }]
      }));
    }
    setLoading(false);
    setLoadingAgent(null);
  };

  const clearChat = () => setConversations(p => ({ ...p, [activeAgent]: [] }));

  return(
    <div className="page" style={{paddingBottom:0,minHeight:"calc(100vh - 52px)",display:"flex",flexDirection:"column"}}>
      <div className="ph" style={{marginBottom:16,flexShrink:0}}>
        <div><div className="ptitle">{platformCtx?.platformModuleNames?.AIWorkspace||(isRTL?"مساحة الذكاء الاصطناعي":"AI Workspace")}</div><div className="psub">{platformCtx?.platformModuleSubtitles?.AIWorkspace||(isRTL?"متعدد النماذج · معزول · لا وصول إلى الملفات":"Multi-model · Isolated · No access to Files")}</div></div>
        <button className="btn btn-s btn-sm" onClick={()=>setShowSettings(true)}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="6.5" cy="6.5" r="1.9"/><path d="M6.5 1v1.2M6.5 10.8V12M1 6.5h1.2M10.8 6.5H12M2.6 2.6l.85.85M9.55 9.55l.85.85M2.6 10.4l.85-.85M9.55 3.45l.85-.85"/></svg>
          {t.aiProviders}
        </button>
      </div>

      {/* AI Provider Settings Modal */}
      {showSettings && (
        <div className="mo" onClick={()=>setShowSettings(false)}>
          <div className="md" style={{maxWidth:580}} onClick={e=>e.stopPropagation()}>
            <div className="mh">
              <div className="mt">{t.aiProviders}</div>
              <button className="btn btn-g btn-sm" onClick={()=>setShowSettings(false)}>✕</button>
            </div>
            <div className="mb">
              <div style={{fontSize:13,color:"var(--g500)",marginBottom:18,lineHeight:1.65}}>
                {t.aiProvidersDesc}
              </div>
              {["anthropic","openai","google"].map(provider => {
                const providerModels = AI_MODELS.filter(m => m.provider === provider);
                const providerNames = {anthropic:"Anthropic (Claude)", openai:"OpenAI (GPT)", google:"Google (Gemini)"};
                const providerColors = {anthropic:"var(--gold,#C9A84C)", openai:"var(--grn,#2D7D52)", google:"var(--blu,#2B5EA7)"};
                const envVars = {anthropic:"ANTHROPIC_API_KEY", openai:"OPENAI_API_KEY", google:"GOOGLE_AI_API_KEY"};
                const isConnected = provider === "anthropic"; // Only Anthropic works in artifact
                return (
                  <div key={provider} style={{marginBottom:20}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:isConnected?"var(--grn)":"var(--g300)",flexShrink:0}}/>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",flex:1}}>{providerNames[provider]}</div>
                      <span className={`pill ${isConnected?"p-done":"p-low"}`}>{isConnected?t.connected:t.notConfigured}</span>
                    </div>
                    <div style={{background:"var(--g50)",border:"1px solid var(--g200)",borderRadius:"var(--r)",padding:"12px 14px",marginBottom:8}}>
                      <div style={{fontSize:11,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:6}}>{t.backendEnvVar}</div>
                      <code style={{fontSize:12,fontFamily:"var(--fm)",color:"var(--ink)",background:"var(--white)",border:"1px solid var(--g200)",borderRadius:4,padding:"4px 8px",display:"inline-block"}}>{envVars[provider]}=sk-...</code>
                    </div>
                    {!isConnected && (
                      <div className="notice" style={{marginBottom:8}}>
                        <div className="notice-l">{t.howToConnect}</div>
                        {isRTL ? <>أضف <code style={{fontFamily:"var(--fm)",fontSize:11}}>{envVars[provider]}</code> إلى متغيرات بيئة Vercel / Supabase. يقرأ الخادم هذا المفتاح ويمرّر الطلبات إلى {providerNames[provider]}. لا تغييرات في الواجهة مطلوبة.</> : <>Add <code style={{fontFamily:"var(--fm)",fontSize:11}}>{envVars[provider]}</code> to your Vercel / Supabase environment variables. Your backend API route reads this key and forwards requests to {providerNames[provider]}. No frontend changes needed.</>}
                      </div>
                    )}
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {providerModels.map(m => (
                        <div key={m.id} style={{display:"flex",alignItems:"center",gap:6,background:"var(--white)",border:"1px solid var(--g200)",borderRadius:"var(--r)",padding:"6px 10px"}}>
                          <div style={{width:6,height:6,borderRadius:"50%",background:m.available?"var(--grn)":"var(--g300)",flexShrink:0}}/>
                          <div>
                            <div style={{fontSize:12,fontWeight:500,color:m.available?"var(--ink)":"var(--g400)"}}>{m.label}</div>
                            <div style={{fontSize:10,color:"var(--g400)",fontFamily:"var(--fm)"}}>{m.model}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mf"><button className="btn btn-g" onClick={()=>setShowSettings(false)}>{t.close}</button></div>
          </div>
        </div>
      )}

      <div className="ai-ws-layout" style={{display:"flex",gap:16,flex:1,overflow:"hidden",paddingBottom:28}}>
        {/* LEFT — agents + prompts */}
        <div className="ai-ws-sidebar" style={{width:220,flexShrink:0,display:"flex",flexDirection:"column",gap:12,overflowY:"auto"}}>
          <div className="card">
            <div className="ch"><div className="ct">{t.agents}</div></div>
            {AGENTS.map(a => {
              const aModelId = agentModels[a.id] || a.defaultModel;
              const aModel = AI_MODELS.find(m => m.id === aModelId);
              const isActive = activeAgent === a.id;
              const msgCount = (conversations[a.id]||[]).length;
              const isLoading = loadingAgent === a.id;
              return (
                <div key={a.id} onClick={()=>setActiveAgent(a.id)} style={{padding:"10px 14px",borderBottom:"1px solid var(--g100)",cursor:"pointer",background:isActive?"var(--g50)":"transparent",transition:"background .08s",borderLeft:isActive?"2px solid var(--ink)":"2px solid transparent"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                    <div style={{fontSize:13,fontWeight:isActive?600:400,color:isActive?"var(--ink)":"var(--g600)",letterSpacing:"-.01em",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {isRTL&&a.labelAr?a.labelAr:a.label}
                    </div>
                    {isLoading&&<div className="ai-dot" style={{width:6,height:6,flexShrink:0}}/>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    {aModel&&<ModelBadge modelId={aModelId} size="xs"/>}
                    {msgCount>0&&<span style={{fontFamily:"var(--fm)",fontSize:9,color:"var(--g400)",marginInlineStart:"auto"}}>{msgCount} {t.msgsCount}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {activeAgent==="custom"&&(
            <div className="card">
              <div className="ch"><div className="ct">{t.systemPrompt}</div><button className="btn btn-g btn-xs" onClick={()=>setEditingCustom(e=>!e)}>{editingCustom?t.doneEditing:t.edit}</button></div>
              <div style={{padding:"10px 14px"}}>
                {editingCustom
                  ?<textarea className="fta" value={customPrompt} onChange={e=>setCustomPrompt(e.target.value)} placeholder={t.defineAgentBehavior} style={{minHeight:100,fontSize:12}}/>
                  :<div style={{fontSize:12,color:customPrompt?"var(--g700)":"var(--g300)",lineHeight:1.6}}>{customPrompt||t.noSystemPrompt}</div>}
              </div>
            </div>
          )}

          <div className="card">
            <div className="ch"><div className="ct">{t.promptLibrary}</div><button className="btn btn-g btn-xs" onClick={()=>setShowAddP(p=>!p)}>+</button></div>
            {showAddP&&(
              <div style={{padding:"10px 14px",borderBottom:"1px solid var(--g100)"}}>
                <input className="fi" value={newPLabel} onChange={e=>setNewPLabel(e.target.value)} placeholder={t.labelPh} style={{marginBottom:6,fontSize:12}}/>
                <textarea className="fta" value={newPText} onChange={e=>setNewPText(e.target.value)} placeholder={t.promptTextPh} style={{minHeight:56,fontSize:12,marginBottom:6}}/>
                <button className="btn btn-p btn-xs" onClick={()=>{if(newPLabel&&newPText){setPromptLib(p=>[...p,{id:`p${Date.now()}`,label:newPLabel,text:newPText}]);setNewPLabel("");setNewPText("");setShowAddP(false);}}}>{t.save}</button>
              </div>
            )}
            {promptLib.map(p=>{
              const lbl=isRTL&&p.labelAr?p.labelAr:p.label;
              const txt=isRTL&&p.textAr?p.textAr:p.text;
              return(
              <div key={p.id} style={{padding:"8px 14px",borderBottom:"1px solid var(--g100)",cursor:"pointer"}} onClick={()=>send(txt)}
                onMouseEnter={e=>e.currentTarget.style.background="var(--g50)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{fontSize:12,fontWeight:500,color:"var(--ink)",marginBottom:2}}>{lbl}</div>
                <div style={{fontSize:11,color:"var(--g400)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{txt}</div>
              </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — chat panel */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {/* Chat header with model selector */}
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexShrink:0,padding:"0 2px"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:15,fontWeight:600,color:"var(--ink)",letterSpacing:"-.01em",marginBottom:2}}>
                {isRTL&&agent.labelAr?agent.labelAr:agent.label}
              </div>
              {activeAgent!=="custom"&&<div style={{fontSize:11,color:"var(--g400)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{agent.system.slice(0,80)}…</div>}
            </div>
            {/* Model selector */}
            <div style={{position:"relative",flexShrink:0}}>
              <div onClick={()=>setShowModelPicker(p=>!p)} style={{display:"flex",alignItems:"center",gap:6,background:"var(--white)",border:"1px solid var(--g200)",borderRadius:"var(--r)",padding:"5px 10px",cursor:"pointer",transition:"border-color .1s"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor="var(--g400)"} onMouseLeave={e=>e.currentTarget.style.borderColor="var(--g200)"}>
                <ModelBadge modelId={selectedModelId} size="xs"/>
                <svg width="9" height="6" viewBox="0 0 9 6" fill="none"><path d="M0 0l4.5 6L9 0z" fill="var(--g400)"/></svg>
              </div>
              {showModelPicker&&(
                <>
                  <div style={{position:"fixed",inset:0,zIndex:199}} onClick={()=>setShowModelPicker(false)}/>
                  <div style={{position:"absolute",top:"100%",right:0,marginTop:4,background:"var(--white)",border:"1px solid var(--g200)",borderRadius:"var(--r2)",boxShadow:"var(--sh2)",zIndex:200,width:240,overflow:"hidden"}}>
                    <div style={{padding:"8px 12px 6px",borderBottom:"1px solid var(--g100)"}}>
                      <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".08em"}}>{t.selectModelFor} {isRTL&&agent.labelAr?agent.labelAr:agent.label}</div>
                    </div>
                    {["anthropic","openai","google"].map(provider=>{
                      const pModels = AI_MODELS.filter(m=>m.provider===provider);
                      const pNames = {anthropic:"Claude",openai:"GPT",google:"Gemini"};
                      return(
                        <div key={provider}>
                          <div style={{padding:"7px 12px 3px",fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".08em",background:"var(--g50)"}}>{pNames[provider]}</div>
                          {pModels.map(m=>(
                            <div key={m.id} onClick={()=>{if(m.available){setAgentModel(activeAgent,m.id);setShowModelPicker(false);}}}
                              style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",cursor:m.available?"pointer":"default",opacity:m.available?1:0.45,transition:"background .08s",background:selectedModelId===m.id?"var(--g50)":"transparent"}}
                              onMouseEnter={e=>{if(m.available)e.currentTarget.style.background="var(--g50)";}} onMouseLeave={e=>e.currentTarget.style.background=selectedModelId===m.id?"var(--g50)":"transparent"}>
                              <div style={{width:6,height:6,borderRadius:"50%",background:m.available?"var(--grn)":"var(--g300)",flexShrink:0}}/>
                              <div style={{flex:1}}>
                                <div style={{fontSize:12,fontWeight:selectedModelId===m.id?600:400,color:m.available?"var(--ink)":"var(--g400)"}}>{m.label}</div>
                                <div style={{fontSize:10,color:"var(--g400)",fontFamily:"var(--fm)"}}>{m.available?t.ready:t.needsApiKeyBackend}</div>
                              </div>
                              {selectedModelId===m.id&&<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--grn)" strokeWidth="1.8"><polyline points="1,6 4.5,9.5 11,2"/></svg>}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            {msgs.length>0&&<button className="btn btn-g btn-sm" onClick={clearChat}>{t.clear}</button>}
          </div>

          {/* Messages */}
          <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:12,paddingInlineEnd:4}}>
            {!msgs.length&&(
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <div style={{textAlign:"center",maxWidth:320}}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="var(--g300)" strokeWidth="1.5" style={{marginBottom:12}}><circle cx="20" cy="20" r="18"/><path d="M13 16c0-3.9 3.1-7 7-7s7 3.1 7 7c0 3.2-2.2 6-5.3 6.8L20 24"/><circle cx="20" cy="30" r="1.5" fill="var(--g300)" stroke="none"/></svg>
                  <div style={{fontSize:14,fontWeight:500,color:"var(--g500)",marginBottom:6}}>{t.startConversation}</div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginBottom:8}}>
                    <span style={{fontSize:12,color:"var(--g400)"}}>{t.using}</span>
                    <ModelBadge modelId={selectedModelId}/>
                  </div>
                  <div style={{fontSize:12,color:"var(--g400)",lineHeight:1.6}}>{t.pickPromptHint}</div>
                </div>
              </div>
            )}
            {msgs.map((m,i)=>(
              <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start",flexDirection:"column",alignItems:m.role==="user"?"flex-end":"flex-start",gap:3}}>
                {m.role==="assistant"&&m.model&&(
                  <div style={{marginInlineStart:4}}><ModelBadge modelId={m.model} size="xs"/></div>
                )}
                <div style={{
                  maxWidth:"74%",
                  background:m.role==="user"?"var(--ink)":"var(--white)",
                  color:m.role==="user"?"white":"var(--ink)",
                  border:m.role==="assistant"?"1px solid var(--g200)":"none",
                  borderRadius:m.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px",
                  padding:"11px 15px",
                  fontSize:13.5,
                  lineHeight:1.7,
                  boxShadow:"var(--sh0)",
                  whiteSpace:"pre-wrap",
                }}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading&&(
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-start",gap:3}}>
                <div style={{marginInlineStart:4}}><ModelBadge modelId={selectedModelId} size="xs"/></div>
                <div style={{background:"var(--white)",border:"1px solid var(--g200)",borderRadius:"14px 14px 14px 4px",padding:"12px 16px",boxShadow:"var(--sh0)"}}>
                  <div className="ai-ld"><div className="ai-db"/><div className="ai-db"/><div className="ai-db"/></div>
                </div>
              </div>
            )}
            <div ref={messagesEnd}/>
          </div>

          {/* Input bar */}
          <div style={{marginTop:12,flexShrink:0,background:"var(--white)",border:"1px solid var(--g200)",borderRadius:"var(--r2)",boxShadow:"var(--sh1)",overflow:"hidden"}}>
            <textarea
              value={input}
              onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder={`${t.messageAgent} ${isRTL&&agent.labelAr?agent.labelAr:agent.label}… ${t.enterToSend}`}
              style={{width:"100%",background:"transparent",border:"none",outline:"none",padding:"13px 16px",fontSize:13.5,fontFamily:"var(--fi)",resize:"none",minHeight:52,maxHeight:140,lineHeight:1.55,color:"var(--ink)"}}
            />
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderTop:"1px solid var(--g100)"}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:11,color:"var(--g400)"}}>{t.sendingWith}</span>
                <ModelBadge modelId={selectedModelId} size="xs"/>
              </div>
              <button className="btn btn-p btn-sm" onClick={()=>send()} disabled={!input.trim()||loading} style={{opacity:(!input.trim()||loading)?0.4:1}}>
                {t.send}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIWorkspace;
